import { useEffect, useState } from 'react';
import { sposServices } from '../services';
import {
  IGetSposContractsParams,
  IImplementSposContract,
  IRegisterSposV2Payload,
  ISposContract,
  ISposContractDetail,
  ISposDevice,
} from '../interfaces';
import {
  commonConstants,
  dateTimeConstants,
  sposContractConstants,
} from '../constants';
import { defaultPage } from '../constants/pagination';
import { AnyObject } from '../interfaces';
import { pickBy } from 'lodash';
import { notification } from 'antd';
import { t } from '../helpers/i18n';
import { datetimeHelpers } from '../helpers';
import moment from 'moment';
import { IUpdateContractAndDevices } from '../interfaces/sposContract';

const { DEFAULT_PARAMS } = commonConstants;
const {
  SPOS_CONTRACT_STATUS,
  SPOS_CONTRACT_STATUS_DISPLAY,
} = sposContractConstants;
const { getFirstDayOfPreviousMonth } = datetimeHelpers;
const { YEAR_DASH_FORMAT } = dateTimeConstants;

const useSposContract = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [haveFirstSposContract, setHaveFirstSposContract] = useState<boolean>(
    false
  );
  const [sposContractId, setSposContractId] = useState<number>();
  const [sposContract, setSposContract] = useState<ISposContractDetail>();
  const [
    loadingImplementSposContract,
    setLoadingImplementSposContract,
  ] = useState<boolean>(false);

  const getHaveFirstSposContract = async (merchantId: number) => {
    try {
      setLoading(true);
      const res = await sposServices.getHaveFirstSposContract(merchantId);
      if (res) {
        setHaveFirstSposContract(res.haveFirstSposContract);
      }
    } finally {
      setLoading(false);
    }
  };

  const registerSposV2 = async (payload: IRegisterSposV2Payload) => {
    let res;
    try {
      setLoading(true);
      res = await sposServices.registerSposV2(payload);
      if (res) {
        setSposContractId(res.sposContractId);
      }
    } finally {
      setLoading(false);
    }
    return res;
  };

  const getSposContract = async (id: number, isGetHistory?: boolean) => {
    try {
      setLoading(true);
      const res = await sposServices.getSposContract(id, isGetHistory);
      if (res) {
        setSposContract(res);
      }
    } finally {
      setLoading(false);
    }
  };

  const implementSposContract = async (payload: IImplementSposContract) => {
    try {
      setLoadingImplementSposContract(true);
      const res = await sposServices.implementSposContract(payload);
      if (res) {
        notification.success({
          message: t('Success'),
          description: t('SposContract.ImplementSposContractSuccess'),
        });
      }
    } finally {
      setLoadingImplementSposContract(false);
    }
  };

  return {
    loading,
    haveFirstSposContract,
    getHaveFirstSposContract,
    sposContractId,
    registerSposV2,
    sposContract,
    getSposContract,
    loadingImplementSposContract,
    implementSposContract,
  };
};

const DEFAULT_PARAMS_GET_SPOS_CONTRACTS = {
  ...DEFAULT_PARAMS,
  getMe: true,
  createdFrom: moment(getFirstDayOfPreviousMonth()).format(YEAR_DASH_FORMAT),
  createdTo: moment().format(YEAR_DASH_FORMAT),
};

const useSposContractsListMobile = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [params, setParams] = useState<IGetSposContractsParams>(
    DEFAULT_PARAMS_GET_SPOS_CONTRACTS
  );
  const [sposContractsList, setSposContractsList] = useState<ISposContract[]>(
    []
  );
  const [hasMore, setHasMore] = useState(true);
  const [totalSposContract, setTotalSposContract] = useState<number>(0);

  const renderQuery = (values: AnyObject) => {
    if (values.sposContractStatus) {
      const found = values.sposContractStatus.find(
        (item: any) =>
          item === SPOS_CONTRACT_STATUS_DISPLAY.WAITING_FOR_APPRAISE.value
      );
      if (found) {
        values.sposContractStatus = [
          ...values.sposContractStatus.filter(
            (item: any) =>
              item !== SPOS_CONTRACT_STATUS_DISPLAY.WAITING_FOR_APPRAISE.value
          ),
          SPOS_CONTRACT_STATUS.APPRAISING.value,
          SPOS_CONTRACT_STATUS.WAITING_FOR_APPROVE.value,
        ];
      }
    }
    return {
      ...pickBy(values, e => {
        return e !== '' && e !== undefined && e !== null;
      }),
    };
  };

  const onSearch = (values: AnyObject) => {
    const query = renderQuery(values);
    setParams({
      ...query,
      getMe: true,
      createdFrom: moment(getFirstDayOfPreviousMonth()).format(
        YEAR_DASH_FORMAT
      ),
      createdTo: moment().format(YEAR_DASH_FORMAT),
      page: defaultPage,
      pageSize: params.pageSize,
    });
  };

  const getSposContractsMobile = async () => {
    try {
      if (params.page === defaultPage) setLoading(true);

      const res = await sposServices.getListContractSpos(params);
      const { sposContracts, pagination } = res;
      setTotalSposContract(pagination.totalItems);
      setHasMore(
        pagination.currentPage * pagination.pageSize < pagination.totalItems
      );
      if (params.page === defaultPage) setSposContractsList(sposContracts);
      else {
        setSposContractsList([...sposContractsList, ...sposContracts]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSposContractsMobile().then();
  }, [params]);

  return {
    loading,
    sposContractsList,
    params,
    setParams,
    hasMore,
    totalSposContract,
    onSearch,
    getSposContractsMobile,
  };
};

const useDeviceSposContract = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [sposDevices, setSposDevices] = useState<ISposDevice[]>([]);

  const getSposDevices = async (sposContractId: number) => {
    let res;
    try {
      setLoading(true);
      res = await sposServices.getSposDevices(sposContractId);
      if (res) {
        setSposDevices(res.devices);
      }
    } finally {
      setLoading(false);
    }
    return res;
  };

  return {
    getSposDevices,
    setSposDevices,
    sposDevices,
    loading,
  };
};

const useContractAndDevices = () => {
  const [loading, setLoading] = useState<boolean>(false);

  const updateSposContractAndDevices = async (
    payload: IUpdateContractAndDevices,
    contractId: number
  ) => {
    setLoading(true);
    try {
      const res = await sposServices.updateContractAndDevices(
        payload,
        contractId
      );
      if (res) {
        notification.success({
          message: t('Success'),
          description: t('UpdateContractAndDevices'),
        });
      }
      return res;
    } finally {
      setLoading(false);
    }
  };

  return { loading, updateSposContractAndDevices };
};

export default {
  useSposContract,
  useSposContractsListMobile,
  useDeviceSposContract,
  useContractAndDevices,
};
